import type { Store as CTStore, StoreQueryResult } from "@graphql/generated/types";
import { COMMERCETOOLS_API_URL } from "@lib/constants";
import { FetchServiceAxios } from "@lib/helpers/fetchServiceAxios";

export interface Store extends Omit<CTStore, "custom"> {
    custom: {
        fields: Record<string, any>;
    };
}

interface StoreResponse extends Omit<StoreQueryResult, "results"> {
    results: Store[];
}

async function fetchStoresWithOffset({
    offset = 0,
    country,
    isStoreLocatorPage,
    accessToken,
}: {
    offset?: number;
    country: string;
    isStoreLocatorPage: boolean;
    accessToken: string;
}): Promise<StoreResponse> {
    const projectKey = process.env.NEXT_PUBLIC_COMMERCETOOLS_PROJECT_KEY!;
    const apiUrl = `${COMMERCETOOLS_API_URL}/${projectKey}/stores`;

    const getWhereClause = () => {
        if (!country) return null;
        if (!isStoreLocatorPage) {
            return `custom(fields(store_CF_Country="${country}") and fields(store_CF_PickupInStore=true))`;
        }
        return `custom(fields(store_CF_Country="${country}"))`;
    };

    const url = new URL(apiUrl);
    url.searchParams.set("limit", "500");
    url.searchParams.set("offset", offset.toString());
    url.searchParams.set("localeProjection", "en");
    url.searchParams.append("expand", "custom.fields.store_CF_OperatingHours");
    url.searchParams.append("expand", "custom.fields.store_CF_ProductMix");

    const where = getWhereClause();
    if (where) {
        url.searchParams.set("where", where);
    }

    return await FetchServiceAxios.get<StoreResponse>(url.toString(), {
        headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
        },
    });
}

export const fetchStores = async (
    country = "",
    isStoreLocatorPage = false,
    accessToken: string
): Promise<Store[]> => {
    const data: Store[] = [];
    const initialResponse = await fetchStoresWithOffset({
        offset: 0,
        country,
        isStoreLocatorPage,
        accessToken,
    });

    if (initialResponse.results.length === 0) {
        return [];
    }

    data.push(...initialResponse.results);
    if (initialResponse.total <= initialResponse.count) {
        return initialResponse.results;
    }
    // Get total pages and subtract the 1st page to get remaining pages
    const remaimingPages = Math.ceil(initialResponse.total / initialResponse.count) - 1;
    const responses = await Promise.all(
        // Loop all remaining pages and fetch them simultaneously
        Array.from({ length: remaimingPages }).map((_, i) =>
            fetchStoresWithOffset({
                offset: (i + 1) * initialResponse.count,
                country,
                isStoreLocatorPage,
                accessToken,
            })
        )
    );
    responses.forEach((response) => {
        data.push(...response.results);
    });

    return data;
};
